import { push } from 'redux-first-history';
import { CheckIcon, ChevronLeftIcon, XIcon } from '@heroicons/react/outline';

import {
  useFinalizeQuizSubmissionMutation,
  useSubmitPracticeMutation,
} from '../services/apiSlice';
import { handleError } from '../services/ErrorService';
import {
  goToProblem,
  hideFinishModal,
  selectIsPractice,
  selectIsTimeUp,
  selectIsSubmitted,
  selectRemainingTime,
  selectSkippedProblems,
  startReviewingQuiz,
  selectQuizType,
  selectAnswerShuffleMap,
} from '../features/quiz/quizSlice';
import { store, useAppSelector } from '../stores/AppStore';
import Button from './Button';
import Vimeo from '@u-wave/react-vimeo';
import {
  congratulationsQuizCompletedVideoUrl,
  getCongratulationsVideoWidth,
  ROLES,
} from '../constants';

export default function Finish() {
  const currentQuiz = useAppSelector((state) => state.quiz.selectedQuiz);
  const isPractice = useAppSelector(selectIsPractice);
  const quizType = useAppSelector(selectQuizType);
  const isPreview = quizType === 'normal' && isPractice;
  const isMentor = useAppSelector(
    (state) => state.user.user?.roles.includes(ROLES.mentor) ?? false,
  );
  const isSubmitted = useAppSelector(selectIsSubmitted);
  const remainingSeconds = useAppSelector(selectRemainingTime);
  const resultId = useAppSelector((state) => state.quiz.resultId);
  const skippedProblems = useAppSelector(selectSkippedProblems);
  const solvedProblems = useAppSelector((state) => state.quiz.solvedProblems);
  const timeIsUp = useAppSelector(selectIsTimeUp);
  const answerShuffleMap = useAppSelector(selectAnswerShuffleMap);
  const [finalizeQuizSubmission, { isLoading: isFinalizingQuizSubmission }] =
    useFinalizeQuizSubmissionMutation();
  const [submitPractice, { isLoading: isSubmittingPractice }] =
    useSubmitPracticeMutation();

  const handleSubmitPractice = async () => {
    if (!currentQuiz || !Array.isArray(solvedProblems)) {
      return;
    }

    try {
      const problems = solvedProblems.map((p) => {
        // Get the shuffle map for this problem (may not exist for teachers)
        const shuffleMap = answerShuffleMap?.[p.id];

        // Handle skipped problems (no answer given)
        if (!p.givenAnswerKey) {
          return {
            problemId: p.id,
            answerKey: undefined,
          };
        }

        // If no shuffle map exists (e.g., for teachers), use the answer key as-is
        if (!shuffleMap) {
          return {
            problemId: p.id,
            answerKey: p.givenAnswerKey,
          };
        }

        // Convert shuffled answer key back to original
        const indexToKey = ['A', 'B', 'C', 'D', 'E'];
        const shuffledIndex = indexToKey.indexOf(p.givenAnswerKey);

        if (shuffledIndex === -1) {
          // Invalid answer key - report error and stop submission
          throw new Error(
            `Invalid answer key: ${p.givenAnswerKey} for problem ${p.id}`,
          );
        }

        // Get the original index from the shuffle map
        const originalIndex = shuffleMap[shuffledIndex];
        const originalAnswerKey = indexToKey[originalIndex];

        return {
          problemId: p.id,
          answerKey: originalAnswerKey,
        };
      });

      await submitPractice({
        trackId: currentQuiz.trackSerialNum,
        quiz: {
          id: currentQuiz.id,
          problems,
        },
        type: quizType ?? 'retake',
        answerDuration: currentQuiz.timeLimit - remainingSeconds,
      });
      // NOTE: status will become submitted so the congratulations modal will show
    } catch (error) {
      handleError(error as Error, {
        displayedError:
          'Sorry, there was an error submitting your practice. Please try again or contact support.',
        tags: {
          quizId: currentQuiz.id,
          trackId: currentQuiz.trackSerialNum,
        },
      });
    }
  };

  if (!currentQuiz || !Array.isArray(solvedProblems)) {
    handleError(new Error('Quiz data is missing at Finish'));
    return <></>; // FIXME
  }

  const startReview = () => {
    store.dispatch(startReviewingQuiz());
    store.dispatch(
      push(`/review/${currentQuiz.trackSerialNum}/${currentQuiz.id}`),
    );
  };

  if (isSubmitted) {
    return (
      <div className="grid col-span-4 grid-cols-3 items-center gap-3">
        {isMentor && (
          <div className="col-span-3 w-full rounded-lg border border-blue-200 bg-blue-50 p-4 text-center text-blue-900">
            <p className="font-bold">
              Mentor {isPreview ? 'preview' : 'practice'} complete
            </p>
            <p className="mt-1 text-sm">
              You can review your answers below. This attempt did not complete
              your Thinker&apos;s assignment or update their progress.
            </p>
          </div>
        )}
        {!isMentor && (
          <div className="grid col-span-3 justify-items-center w-full">
            <Vimeo
              video={congratulationsQuizCompletedVideoUrl}
              width={getCongratulationsVideoWidth()}
              controls={false}
              autoplay={true}
              onEnd={startReview}
            />
          </div>
        )}
        <div className="grid col-start-2 col-span-1 justify-items-center items-center">
          <Button
            action={startReview}
            bgColor={'bg-blue-300'}
            bgColorHover={'bg-blue-400'}
            buttonText={'Review your solutions'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid col-span-4 grid-cols-3 items-center gap-3">
      <div className="grid col-span-3 justify-items-center ">
        {skippedProblems.length > 0 ? (
          <p className="pb-5 text-lg">You skipped the following problems:</p>
        ) : (
          <div className="flex">
            <CheckIcon className="h-8 w-8 text-tttDefault pr-2" />
            <p className="text-lg">You didn't skip any problems</p>
          </div>
        )}
      </div>
      {timeIsUp ? (
        <></>
      ) : (
        <button
          disabled={isFinalizingQuizSubmission || isSubmittingPractice}
          className="col-start-1 col-span-1 justify-items-start items-center"
        >
          <ChevronLeftIcon
            className="h-8 w-8 text-tttDefault"
            onClick={() => store.dispatch(hideFinishModal())}
          />
        </button>
      )}
      <div className="grid col-start-2 col-span-1 justify-items-center items-center">
        {skippedProblems.map((problem, index) => (
          <div
            key={`skippedproblems-${problem.id}`}
            className="grid text-lg justify-items-center items-center w-full"
          >
            {timeIsUp ? (
              <div className="grid justify-items-start items-center w-full">
                <div className="flex">
                  <XIcon className="h-7 w-7 text-tttDefault pr-2" />
                  Problem {index + 1}
                </div>
              </div>
            ) : (
              <div className="grid justify-items-start items-center text-start w-full">
                <div className="flex">
                  <button
                    onClick={() => store.dispatch(goToProblem(problem.num))}
                    disabled={
                      isFinalizingQuizSubmission || isSubmittingPractice
                    }
                    className="flex"
                  >
                    <XIcon className="h-7 w-7 text-tttDefault pr-2" />
                    Problem {problem.num + 1}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {skippedProblems.length > 0 && remainingSeconds > 60 && !isPractice && (
          <p className="py-5 text-tttDefault">
            Tip: You still have time to go back and try to solve a skipped
            problem.
          </p>
        )}
      </div>
      <div className="grid col-start-1 md:col-start-3 col-span-3 md:col-span-1 justify-items-center md:justify-items-end items-center md:items-end h-full">
        {isPractice ? (
          <Button
            action={handleSubmitPractice}
            bgColor={'bg-blue-300'}
            bgColorHover={'bg-blue-400'}
            buttonText={
              isMentor
                ? isPreview
                  ? 'View Preview Results'
                  : 'View Practice Results'
                : isPreview
                ? 'Submit Preview Quiz'
                : 'Submit practice'
            }
            disabled={isSubmittingPractice}
          />
        ) : (
          <Button
            action={async () => {
              await finalizeQuizSubmission({
                resultId: resultId ?? '',
                quiz: {
                  id: currentQuiz.id,
                  problems: solvedProblems.map((p) => ({
                    problemId: p.id,
                    answerKey: p.givenAnswerKey,
                  })),
                },
              });
              // NOTE: status will become submitted so the congratulations modal will show
            }}
            bgColor={'bg-blue-300'}
            bgColorHover={'bg-blue-400'}
            buttonText={'Submit'}
            disabled={isFinalizingQuizSubmission}
          />
        )}
      </div>
    </div>
  );
}
